import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, PlusCircle, ChevronRight, Timer, RefreshCw, Loader2, UserCheck, Clock, Trash2, AlertTriangle, Mail, PhoneCall, CalendarClock } from "lucide-react";
import api, { STATUS_LABELS, STATUS_COLORS, ONBOARDING_STAGE_LABELS, ONBOARDING_STAGE_COLORS, statusLabelForLead, getUser } from "@/lib/api";
import LeadPreviewModal from "@/components/LeadPreviewModal";

const SLA_BADGE = {
  ok:      "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] border-[hsl(var(--success))]/25",
  warn:    "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/25",
  urgent:  "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/25",
  overdue: "bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] border-[hsl(var(--danger))]/25",
};

// SLA "não tratado" só se aplica em horário útil:
//   • Segunda a Sábado, das 09:30 às 20:00 (hora local Lisboa).
//   • Fora desta janela nada é considerado violação — o cronómetro
//     também pausa (se o lead entrou a meio da noite, o tempo é
//     contado apenas a partir das 09:30 do próximo dia útil).
//
// 25/02/2026 — Regra do "dia operacional": os leads que entram após
// as 20h contam para o SEPARADOR HOJE do dia útil seguinte. Sábado
// >=20h + Domingo → Segunda. O filtro do separador é feito no
// backend (`GET /api/leads?period=today`) — aqui apenas calculamos o
// badge de SLA para os leads devolvidos.
const BIZ_START_MIN = 9 * 60 + 30;   // 09:30
const BIZ_END_MIN = 20 * 60;         // 20:00

function _bizMinsOfDay(d) {
  return d.getHours() * 60 + d.getMinutes();
}
function _isBusinessDay(d) {
  const w = d.getDay();  // 0 = Sun
  return w >= 1 && w <= 6;
}
function _isInBusinessWindow(d) {
  if (!_isBusinessDay(d)) return false;
  const m = _bizMinsOfDay(d);
  return m >= BIZ_START_MIN && m < BIZ_END_MIN;
}

// Untreated leads: qualificacao_inicial AND nunca alguém tocou no lead.
// Assim que qualquer utilizador (SAC, gestor, admin) abre/edita/claim o
// lead, o `handling_since` fica preenchido para sempre — mesmo quando o
// heartbeat de "em tratamento" expira 2 min depois. Isso é o que
// interessa aqui: o badge só deve pintar leads virgens.
// 0-15min: ok (green) · 15min-2h: warn (orange) · >2h: overdue (red)
function slaFor(lead) {
  // Leads de escritórios de parceiros (Lisboa) não têm SLA — read-only.
  if (lead.office === "Lisboa") return null;
  if (lead.status !== "qualificacao_inicial" || !lead.created_at) return null;
  if (lead.is_being_handled) return null;      // alguém está a ver agora
  if (lead.handling_since) return null;         // alguém já claimou em algum momento
  if (lead.first_contact_at) return null;       // primeiro contacto SAC registado

  const now = new Date();
  // Fora do horário útil não há violação nenhuma.
  if (!_isInBusinessWindow(now)) return null;

  const created = new Date(lead.created_at);
  if (isNaN(created)) return null;

  // Se o lead entrou hoje antes das 09:30, o cronómetro só começa a
  // contar às 09:30. Se entrou fora do horário útil de um dia
  // anterior, aproximamos ao inicio de hoje (suficiente para o filtro
  // "Hoje" onde created_at é do próprio dia). Fim-de-semana entradas
  // são tratadas do mesmo modo — só pesam quando volta a haver janela
  // útil.
  const bizStartToday = new Date(now);
  bizStartToday.setHours(9, 30, 0, 0);
  const effectiveStart = created < bizStartToday ? bizStartToday : created;
  const mins = Math.max(0, Math.floor((now.getTime() - effectiveStart.getTime()) / 60000));

  let level = "ok";
  if (mins >= 120) level = "overdue";       // > 2h → vermelho
  else if (mins >= 15) level = "warn";      // 15m-2h → laranja
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const label = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return { mins, level, label };
}

// Callback SAC — email de "Tentativa de contacto" enviado ao cliente mas
// sem resposta. Escala em 3 níveis: pending (<48h, cinza-âmbar) · warn
// (48-72h) · overdue (>72h). Depois do cliente responder ou desistir
// deixa de aparecer.
function callbackFor(lead) {
  if (!lead.callback_email_sent_at) return null;
  if (lead.callback_response) return null;
  const sent = new Date(lead.callback_email_sent_at);
  if (isNaN(sent)) return null;
  const hours = Math.max(0, Math.floor((Date.now() - sent.getTime()) / 3600000));
  let level = "pending";
  if (hours >= 72) level = "overdue";
  else if (hours >= 48) level = "warn";
  const days = Math.floor(hours / 24);
  const label = days >= 1 ? `${days}d ${hours % 24}h` : `${hours}h`;
  return { hours, level, label };
}

const CALLBACK_BADGE = {
  pending: "bg-muted/40 text-muted-foreground border-border",
  warn:    "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40",
  overdue: "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-orange-400 font-semibold",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [period, setPeriod] = useState("today");
  const [periodCounts, setPeriodCounts] = useState({});   // { today: N, yesterday: N, ... }
  const [, setTick] = useState(0);
  const PAGE_SIZE = 50;
  const debounceRef = useRef(null);

  const load = async (opts = {}) => {
    const p = opts.period ?? period;
    const s = opts.q ?? q;
    const st = opts.statusFilter ?? statusFilter;
    const offset = opts.offset ?? 0;
    const params = new URLSearchParams({ period: p, limit: String(PAGE_SIZE), offset: String(offset) });
    if (s.trim()) params.set("search", s.trim());
    if (st) params.set("status", st);
    const r = await api.get(`/leads?${params.toString()}`);
    const items = r.data.items || [];
    setTotal(r.data.total || 0);
    setHasMore(!!r.data.has_more);
    setLeads(prev => offset === 0 ? items : [...prev, ...items]);
  };

  // 26/02/2026 — Fetch counts for all 5 period tabs so we can render
  // "Hoje (12)" / "Ontem (8)"… labels next to each button. Respects the
  // active status + search filters so the numbers match the current view.
  const loadPeriodCounts = async (opts = {}) => {
    const s = opts.q ?? q;
    const st = opts.statusFilter ?? statusFilter;
    const params = new URLSearchParams();
    if (s.trim()) params.set("search", s.trim());
    if (st) params.set("status", st);
    try {
      const r = await api.get(`/leads/period-counts?${params.toString()}`);
      setPeriodCounts(r.data?.counts || {});
    } catch (e) {
      setPeriodCounts({});
    }
  };

  // 04/08/2026 — Pull-through inbox sync
  // The background loop only runs every 3 min, so a customer emailing
  // seconds before the SAC opens this page would not appear in "Hoje"
  // until the next sweep. This fire-and-forget call refreshes the
  // mailbox first, then reloads the leads list so recurring clients
  // aparecem imediatamente. The backend guarda um cooldown de 20s
  // partilhado por todos os utilizadores, portanto é seguro chamar em
  // cada mount ou clique de refresh.
  const [inboxSyncing, setInboxSyncing] = useState(false);
  const refreshInbox = async ({ silent = true } = {}) => {
    setInboxSyncing(true);
    try {
      const { data } = await api.post("/inbox-sync/refresh");
      if (!silent) {
        if (data?.ok === false) {
          toast.warning("Não foi possível sincronizar o email agora — tenta daqui a pouco.");
        } else if (data?.cooldown) {
          toast.info("Emails já foram sincronizados recentemente.");
        } else {
          const matched = data?.matched || 0;
          toast.success(matched > 0
            ? `Sincronização: ${matched} email(s) novos associados a leads`
            : "Sincronização concluída — sem novos emails");
        }
      }
    } catch (_e) { /* silent — the SAC continua a ver o snapshot anterior */ }
    finally { setInboxSyncing(false); }
  };

  // Initial + refetch on period change. On the very first mount we
  // proactively pull new inbox emails so recurring clients that just
  // emailed appear on the SAC's "Hoje" tab immediately.
  useEffect(() => {
    let alive = true;
    (async () => {
      await refreshInbox({ silent: true });
      if (alive) await load({ offset: 0 });
    })();
    return () => { alive = false; };
    /* eslint-disable-next-line */
  }, [period, statusFilter]);
  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load({ offset: 0 }), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [q]);
  // 26/02/2026 — Refresh period counts whenever filters or search change
  // (excluding `period` — the counts already cover every tab).
  useEffect(() => {
    loadPeriodCounts();
    /* eslint-disable-next-line */
  }, [statusFilter, q]);
  // SLA tick + soft refresh of first page every 30s
  useEffect(() => {
    const id = setInterval(() => {
      setTick(t => t + 1);
      load({ offset: 0 });
      loadPeriodCounts();
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [period, statusFilter, q]);

  const currentUser = getUser();
  const canDelete = currentUser?.role === "admin" || currentUser?.role === "gestor";
  // The "Preview antes de ligar" briefing modal is exclusively for the
  // Atendimento (SAC) role — gestores/admins skip straight to the lead.
  const canPreview = currentUser?.role === "atendimento" || currentUser?.role === "admin";
  const [previewLead, setPreviewLead] = useState(null);
  const deleteLead = async (e, lead) => {
    e.preventDefault(); e.stopPropagation();
    const name = lead?.qualification?.contact?.name || "este lead";
    const hasZoho = !!lead.zoho_lead_id;
    const alsoZoho = hasZoho && window.confirm(
      `Eliminar "${name}".\n\nEste lead também existe no Zoho.\n\nOK = eliminar aqui E no Zoho\nCancelar = eliminar só aqui`
    );
    if (!hasZoho && !window.confirm(`Eliminar "${name}"?`)) return;
    try {
      const { data } = await api.delete(`/leads/${lead.id}`, { params: { also_zoho: alsoZoho } });
      if (data.zoho?.attempted && !data.zoho.ok) toast.warning(`Aqui: OK · Zoho: falhou (${data.zoho.error || "erro"})`);
      else toast.success(data.zoho?.attempted ? "Eliminado (aqui + Zoho)" : "Lead eliminado");
      setLeads(ls => ls.filter(x => x.id !== lead.id));
    } catch(err){ toast.error(err.response?.data?.detail || "Erro"); }
  };

  // Infinite-scroll sentinel
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(async (entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !loadingMore) {
        setLoadingMore(true);
        try { await load({ offset: leads.length }); }
        finally { setLoadingMore(false); }
      }
    }, { rootMargin: "300px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
    // eslint-disable-next-line
  }, [hasMore, loadingMore, leads.length]);
  const [importing, setImporting] = useState(false);
  const [importInfo, setImportInfo] = useState(null);
  useEffect(() => {
    api.get("/zoho/import-status").then((r) => setImportInfo(r.data)).catch(() => {});
  }, []);
  const importNow = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/zoho/import");
      toast.success(`Importação: ${data.imported} novas · ${data.updated} atualizadas`);
      await Promise.all([load({ offset: 0 }), api.get("/zoho/import-status").then((r) => setImportInfo(r.data))]);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha na importação"); }
    finally { setImporting(false); }
  };

  // Server does filtering now (search+status+period). Keep local alias.
  const filtered = leads;

  return (
    <div className="p-8 lg:p-12 max-w-[1400px] mx-auto" data-testid="leads-root">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Cliente</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Leads</h1>
          {importInfo?.enabled && importInfo?.last_import_at && (
            <div className="text-[11px] text-muted-foreground mt-2">
              Última importação Zoho: {new Date(importInfo.last_import_at).toLocaleString('pt-PT')}
              {importInfo.auto_import_enabled && ` · auto ${importInfo.auto_import_interval_minutes}min`}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="leads-refresh-inbox"
            onClick={async () => { await refreshInbox({ silent: false }); await load({ offset: 0 }); }}
            disabled={inboxSyncing}
            title="Puxar emails novos e recarregar a lista"
            className="inline-flex items-center gap-2 bg-card hover:bg-muted border border-border text-muted-foreground font-semibold rounded-full px-4 py-2.5 text-sm"
          >
            {inboxSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Sincronizar emails
          </button>
          {importInfo?.enabled && (
            <button data-testid="leads-import-zoho" onClick={importNow} disabled={importing}
              className="inline-flex items-center gap-2 bg-card hover:bg-muted border border-border text-muted-foreground font-semibold rounded-full px-4 py-2.5 text-sm">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Importar Zoho
            </button>
          )}
          <Link data-testid="leads-new-btn" to="/leads/new" className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-md px-4 py-2 text-sm transition-all font-heading tracking-tight">
            <PlusCircle className="w-4 h-4" /> Nova qualificação
          </Link>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-3 text-muted-foreground/70" />
          <input data-testid="leads-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Pesquisar por nome, telefone ou email..."
            className="w-full bg-card border border-border rounded-full pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50" />
        </div>
        <select data-testid="leads-status-filter" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}
          className="bg-card border border-border rounded-full px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50">
          <option value="">Todos os estados</option>
          {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="flex items-center gap-1 mb-6 bg-card border border-border rounded-full p-1 w-fit">
        {[
          {k:"today", t:"Hoje"},
          {k:"yesterday", t:"Ontem"},
          {k:"week", t:"Esta semana"},
          {k:"month", t:"Este mês"},
          {k:"all", t:"Tudo"},
        ].map(p => {
          const cnt = periodCounts[p.k];
          const showCount = typeof cnt === "number";
          const active = period === p.k;
          return (
            <button key={p.k} data-testid={`leads-period-${p.k}`} onClick={()=>setPeriod(p.k)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all inline-flex items-center gap-1.5 ${
                active ? "bg-foreground text-background shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}>
              {p.t}
              {showCount && (
                <span
                  data-testid={`leads-period-${p.k}-count`}
                  className={`inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
                    active
                      ? "bg-background/15 text-background"
                      : "bg-muted text-foreground/80"
                  }`}
                >
                  {cnt}
                </span>
              )}
            </button>
          );
        })}
        <span className="ml-3 text-[11px] text-muted-foreground pr-3" data-testid="leads-count">{leads.length} de {total} leads</span>
      </div>

      {/* 25/02/2026 — Weekend Volume Insight banner: only aparece na tab
          "Ontem" e sublinha o número de leads que entraram fora do horário
          útil (mais destaque à Segunda de manhã, quando o intervalo cobre
          Sábado 19h → Domingo 23h59). Ajuda o SAC a priorizar o backlog
          do fim-de-semana antes de mergulhar no dia corrente. */}
      {period === "yesterday" && total > 0 && (() => {
        const wd = new Date().getDay(); // 0=Sun..6=Sat
        const isMondayMorning = wd === 1;
        const title = isMondayMorning
          ? `${total} lead${total === 1 ? "" : "s"} chegaram durante o fim-de-semana`
          : `${total} lead${total === 1 ? "" : "s"} entraram ontem`;
        const subtitle = isMondayMorning
          ? "Sábado a partir das 19:00 · Domingo. Trabalha estes primeiro antes de mergulhar no dia de hoje."
          : "Revê antes de avançar para os leads de hoje — pode haver clientes à espera de resposta.";
        return (
          <div
            className={`mb-4 rounded-2xl border px-5 py-4 flex items-center gap-4 ${isMondayMorning
              ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))]"
              : "border-primary/30 bg-primary/5"}`}
            data-testid="weekend-volume-banner"
          >
            <div className={`p-2 rounded-full ${isMondayMorning
              ? "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"
              : "bg-primary/10 text-primary"}`}>
              <CalendarClock className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold" data-testid="weekend-volume-title">{title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
            </div>
          </div>
        );
      })()}

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              <th className="text-left px-6 py-4">Contacto</th>
              <th className="text-left px-6 py-4 hidden md:table-cell">Localização</th>
              <th className="text-left px-6 py-4 hidden xl:table-cell">Entrou</th>
              <th className="text-left px-6 py-4 hidden lg:table-cell">Em tratamento</th>
              <th className="text-left px-6 py-4">Estado</th>
              <th className="text-right px-6 py-4">Valor</th>
              <th className="w-10 px-6 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => {
              const c = l.qualification?.contact || {};
              const loc = l.qualification?.location || {};
              const price = l.proposal?.monthly_price_max || l.estimate?.monthly_price_max || 0;
              const created = l.created_at ? new Date(l.created_at) : null;
              const createdTxt = created && !isNaN(created)
                ? `${String(created.getDate()).padStart(2,"0")}/${String(created.getMonth()+1).padStart(2,"0")} · ${String(created.getHours()).padStart(2,"0")}:${String(created.getMinutes()).padStart(2,"0")}`
                : "—";
              const s = slaFor(l);
              const cb = callbackFor(l);
              // No separador "Hoje", pinta a fundo AMARELO qualquer lead
              // ainda por tratar (sem primeiro contacto SAC, sem claim). O
              // border-left continua a graduar a urgência (amber<2h,
              // red>2h). Fora do separador Hoje mantemos só o border-left
              // para não sobrecarregar a lista de dias anteriores.
              // 25/02/2026 — utilizador pediu explicitamente amarelo em
              // vez de laranja para diferenciar visualmente dos alertas
              // vermelhos (overdue) que continuam a laranja/vermelho no
              // border-left.
              const untreated = !!s && period === "today";
              const rowCls = untreated
                ? (s.level === "overdue"
                    ? "border-t border-[hsl(var(--warning))]/40 border-l-4 border-l-red-600 bg-[hsl(var(--warning-bg))] hover:bg-[hsl(var(--warning))]/40"
                    : "border-t border-[hsl(var(--warning))]/40 border-l-4 border-l-yellow-600 bg-[hsl(var(--warning-bg))] hover:bg-[hsl(var(--warning))]/40")
                : s?.level === "overdue"
                  ? "border-t border-border border-l-4 border-l-red-500 hover:bg-muted/40"
                  : s?.level === "warn"
                    ? "border-t border-border border-l-4 border-l-amber-500 hover:bg-muted/40"
                    : "border-t border-border/60 hover:bg-muted/40";
              return (
                <tr key={l.id} className={`${rowCls} transition-colors`} data-testid={`lead-row-tr-${l.id}`} data-untreated={untreated ? "1" : "0"}>
                  <td className="px-6 py-4">
                    <Link data-testid={`lead-row-${l.id}`} to={`/leads/${l.id}`} className="block">
                      <div className="text-sm font-medium flex items-center gap-2">
                        {c.name}
                        {l.inbound_email_count > 0 && (
                          <span
                            data-testid={`inbound-badge-${l.id}`}
                            className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 ${
                              l.has_new_inbound_email
                                ? "bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] border border-[hsl(var(--danger))]/40"
                                : "bg-muted text-foreground border border-border"
                            }`}
                            title={l.has_new_inbound_email
                              ? `Cliente enviou ${l.inbound_email_count} email(s) — novo desde o último acesso`
                              : `Cliente enviou ${l.inbound_email_count} email(s)`}
                          >
                            {l.has_new_inbound_email && (
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                            )}
                            <Mail className="w-2.5 h-2.5" />
                            {l.inbound_email_count}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{c.phone} · {c.email || "—"}</div>
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground hidden md:table-cell">{loc.municipality}{loc.district && `, ${loc.district}`}</td>
                  <td className="px-6 py-4 text-xs text-muted-foreground hidden xl:table-cell tabular-nums" data-testid={`lead-time-${l.id}`}>
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3 text-muted-foreground/70" /> {createdTxt}</span>
                  </td>
                  <td className="px-6 py-4 hidden lg:table-cell" data-testid={`lead-handling-${l.id}`}>
                    {l.is_being_handled && l.handling_user_name ? (
                      /* LIVE — someone is actively viewing the lead right now
                         (heartbeat active). Highest priority, green pill. */
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] text-[#059669] text-[10px] uppercase tracking-[0.15em] font-semibold"
                            title="Está com este lead aberto agora">
                        <UserCheck className="w-3 h-3" /> {l.handling_user_name}
                      </span>
                    ) : l.assigned_manager_name ? (
                      /* Gestor atribuído para Reunião Técnica — próximo owner do lead. */
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#BFDBFE] bg-background text-primary text-[10px] uppercase tracking-[0.15em] font-semibold"
                            title="Gestor atribuído para a Reunião Técnica">
                        <UserCheck className="w-3 h-3" /> {l.assigned_manager_name}
                      </span>
                    ) : l.first_contact_by_name ? (
                      /* SAC operador que fez o primeiro contacto — responsável enquanto
                         não é atribuído a um gestor. */
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-border bg-muted text-foreground text-[10px] uppercase tracking-[0.15em] font-semibold"
                            title="Operador SAC que fez o primeiro contacto">
                        <UserCheck className="w-3 h-3" /> {l.first_contact_by_name}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/70">Disponível</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Fase interna do workflow Caring — fonte única de
                          verdade para o pill principal. Coerente com o
                          cabeçalho do LeadDetail. O estado bruto do Zoho
                          aparece por baixo como badge secundário quando é
                          diferente. */}
                      <span className={STATUS_COLORS[l.status] || 'shift-chip'}
                            title="Fase interna do workflow Caring"
                            data-testid={`lead-status-${l.id}`}>
                        {statusLabelForLead(l)}
                      </span>
                      {/* 25/02/2026 — Badge do onboarding pós-adjudicação
                          (b3 do novo funil). Só aparece quando aplicável. */}
                      {l.onboarding_stage && ONBOARDING_STAGE_LABELS[l.onboarding_stage] && (
                        <span
                          className={ONBOARDING_STAGE_COLORS[l.onboarding_stage] || 'shift-chip'}
                          title="Fase do onboarding pós-adjudicação (caução, PI, arranque)"
                          data-testid={`lead-onboarding-stage-${l.id}`}
                        >
                          {ONBOARDING_STAGE_LABELS[l.onboarding_stage]}
                        </span>
                      )}
                      {l.zoho_lead_status && l.zoho_lead_status.toLowerCase() !== (STATUS_LABELS[l.status] || '').toLowerCase() && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border border-border bg-muted/40 text-muted-foreground"
                              title="Estado bruto sincronizado do Zoho CRM"
                              data-testid={`lead-zoho-status-${l.id}`}>
                          <span className="text-muted-foreground">Zoho:</span> {l.zoho_lead_status}
                        </span>
                      )}
                      {l.zoho_converted && (
                        <span className="inline-flex text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border border-border bg-muted text-foreground font-semibold"
                              title={l.zoho_converted_at ? `Convertido no Zoho em ${new Date(l.zoho_converted_at).toLocaleString('pt-PT')}` : 'Lead convertido no Zoho'}
                              data-testid={`lead-converted-${l.id}`}>
                          Convertido
                        </span>
                      )}
                      {s && s.level !== "ok" && (
                        <span data-testid={`sla-badge-${l.id}`}
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border font-semibold ${SLA_BADGE[s.level]}`}>
                          <AlertTriangle className="w-3 h-3" /> Não tratado há {s.label}
                        </span>
                      )}
                      {s && s.level === "ok" && (
                        <span data-testid={`sla-badge-${l.id}`}
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${SLA_BADGE.ok}`}>
                          <Timer className="w-3 h-3" /> {s.label}
                        </span>
                      )}
                      {cb && (
                        <span data-testid={`callback-badge-${l.id}`}
                              title={`Email de tentativa de contacto enviado ao cliente há ${cb.label} — sem resposta ainda.`}
                              className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${CALLBACK_BADGE[cb.level]}`}>
                          <Mail className="w-3 h-3" />
                          {cb.level === "overdue"
                            ? `Sem resposta há ${cb.label}`
                            : cb.level === "warn"
                              ? `Aguarda resposta · ${cb.label}`
                              : `Callback enviado · ${cb.label}`}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right text-sm tabular-nums">{price ? `${price.toLocaleString('pt-PT')} €` : "—"}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canPreview && l.inbound_email_count > 0 && (
                        <button
                          data-testid={`preview-lead-${l.id}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPreviewLead(l); }}
                          className="p-1 rounded text-primary hover:text-[#1E40AF] hover:bg-[hsl(var(--info-bg))]"
                          title="Preview antes de ligar (20s de briefing)"
                        >
                          <PhoneCall className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button data-testid={`delete-lead-${l.id}`} onClick={(e)=>deleteLead(e,l)} className="text-muted-foreground/70 hover:text-rose-500 p-1" title="Eliminar lead">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      <Link
                        to={`/leads/${l.id}`}
                        data-testid={`open-lead-${l.id}`}
                        title="Abrir lead"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted hover:bg-primary text-foreground hover:text-primary-foreground border border-border hover:border-primary transition-colors"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-muted-foreground text-sm">Sem leads registados.</td></tr>
            )}
          </tbody>
        </table>
        <div ref={sentinelRef} data-testid="leads-sentinel" className="py-6 text-center text-xs text-muted-foreground">
          {loadingMore && <span className="inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> A carregar mais…</span>}
          {!hasMore && leads.length > 0 && <span>— fim da lista —</span>}
        </div>
      </div>
      {/* Pre-call briefing modal — Atendimento only, opens from the phone
          icon on rows with inbound email. Timer is dropped on close. */}
      <LeadPreviewModal
        open={!!previewLead}
        leadId={previewLead?.id}
        leadPreview={previewLead}
        onClose={() => setPreviewLead(null)}
      />
    </div>
  );
}
